<template>
    <div class="report">
        <div class="is-flex is-justify-content-space-between">
            <div class="has-text-left">
                <div v-if="breakdown">
                    <breadcrumb :crumbs="breakdown.breadcrumbs"></breadcrumb>
                </div>
            </div>
            <div class="has-text-right">
                <a href="javascript://" @click="OpenGlossary">How are dashboard metrics calculated?</a>
            </div>
        </div>
        <report-filter v-show="!widget" :editing="editFilter" :report="report" :autoRun="autoRun && !forceNoAutoRun" :options="reportOptions" @beforeExport="PrepareColumns" @search="AwaitGetReport" @edit="editFilter = true"></report-filter>
        <h2 style="margin-left: 5px" v-if="!report.loading && report?.results?.length == 0">No results</h2>
        <dashboard-facade v-if="report.loading && !loadingFacade && facade" :data="facade"></dashboard-facade>
        <loading v-if="report.loading"></loading>
        <div v-if="!report.loading && !editFilter">
            <metrics :report="report" :details="boydenianDetails" :breakdown="breakdown" class="mt-5"></metrics>
            <compare class="mt-5" :report="report" :breakdown="breakdown" :sample-size="7" v-if="hasRan"></compare>
            <breakdown :report="report" @change="SetBreakdown" showFilledJobValue class="mt-5"></breakdown>
        </div>
    </div>
</template>
<script>
    import { ref, onMounted, inject, watch, nextTick, computed } from "vue";
    import { useRoute } from "vue-router";
    import { share, reportFilter, breakdown, metrics } from "@/components/webComponents";
    import { GenericReport } from "@/assets/types";
    import Loading from "@/components/loading.vue";
    import compare from "@/components/webComponents/charts/compare.vue";
    import glossaryTemplate from "@/components/webComponents/template/how_are_dashboard_metrics_calculated.vue";
    import store from "@/store";
    import breadcrumb from "@/components/webComponents/breadcrumb/breadcrumb.vue";
    import dashboardFacade from "@/components/webComponents/dashboardFacade/dashboardFacade.vue";
    import { kpi_datasets } from "@/components/webComponents/charts/kpiOutput.js";
    import { compare_datasets } from "@/components/webComponents/charts/compareOutput.js";
    import { breakdown_output } from "@/components/webComponents/breakdownOutput.js";
    import { metrics_output } from "@/components/webComponents/metricsOutput.js";

    export default {
        components: {
            reportFilter,
            metrics,
            breakdown,
            Loading,
            compare,
            breadcrumb,
            dashboardFacade
        },
        props: {
            filters: {
                type: Object,
                default: () => {
                    return undefined;
                }
            },
            autoRun: {
                type: Boolean,
                default: false
            },
            widget: {
                type: Boolean,
                default: false
            }
        },
        setup(props, context) {
            const $financial = inject("$financial");
            const moment = inject("$moment");
            const route = useRoute();
            const http = inject("$http");
            const popup = inject("$popup");
            const $error = inject("$error");
            const debouncer = inject("$debouncer");

            let cancellationToken = http.CancelToken.source();
            const hasRan = ref(false);
            const options = ref({
                filterTypes: [
                    { Id: "activity", Name: "Activity" },
                    { Id: "taken", Name: "Date Taken" },
                    { Id: "invoice", Name: "Invoice Date" },
                    { Id: "status", Name: "Status Date" }
                ]
            });
            const breakdown = ref();
            const boydenianDetails = ref({});
            const editFilter = ref(true);
            const reportLastRun = ref();
            const initialized = ref(false);
            const running = ref(false);
            const reportOptions = ref({
                title: "Dashboard",
                share: true,
                excel: true,
                sheets: []
            });
            const facade = ref({});
            const loadingFacade = ref(false);

            const report = ref(new GenericReport("Financial"));
            // report.value.filter.date = {
            // 	selection: "YTD",
            // 	startDate: moment.utc().startOf("year").startOf("day").toDate(),
            // 	endDate: moment.utc().endOf("day").toDate()
            // };

            const forceNoAutoRun = computed(() => {
                return route.query && route.query.autorun == "false";
            });

            const InitializeReport = async () => {
                if (props.filters) {
                    report.value.filter = props.filters;
                }

                if (route.query && route.query.reportId) {
                    report.value.Id = Number.parseInt(route.query.reportId);
                }

                await TryGetReportByStub();

                report.value.cancelToken = cancellationToken;
                initialized.value = true;
            };

            const GetReport = async (params) => {
                if (!initialized.value) return false;
                //if (running.value == true) return false;
                running.value = true;
                loadingFacade.value = true;

                cancellationToken.cancel("Created a new request.");
                cancellationToken = http.CancelToken.source();
                report.value.cancelToken = cancellationToken;

                await GetFacade();
                loadingFacade.value = false;

                editFilter.value = false;
                report.value.loading = true;
                //if (route.query && !route.query.stub) {
                report.value.Id = 0; //Always generate a new stub
                //}

                let reportOptions = {};
                if (route.query && route.query.refresh) {
                    reportOptions.refresh = true;
                }
                reportOptions = { ...reportOptions, ...(params || {}) };

                const response = await Promise.all([
                    $financial.getReport(report.value, { ...{ saveReport: true }, ...reportOptions }),
                    $financial.getCompare(report.value, reportOptions),
                    $financial.getBoydenianDetails(report.value)
                ]);
                boydenianDetails.value = response[2];
                report.value.loading = false;
                hasRan.value = true;
                reportLastRun.value = new Date().toLocaleString("en-us");
                running.value = false;

                const isCancelled = report.value.cancelToken && report.value.cancelToken.token.reason != undefined && report.value.cancelToken.token.reason.message?.length > 0;
                // if (report.value.results && report.value.results.length == 0 && !isCancelled) {
                //     $error(
                //         "No results for this combination of filters and time range, please adjust and rerun.", 10000
                //     );
                // }
            };

            const AwaitGetReport = async (params) => {
                if (!initialized.value) {
                    let poll;
                    await new Promise((resolve) => {
                        poll = setInterval(() => {
                            if (initialized.value) {
                                clearInterval(poll);
                                resolve();
                            }
                        }, 500);
                    }).then(async () => {
                        await GetReport(params);
                    });
                } else {
                    await GetReport(params);
                }
            };

            const TryGetReportByStub = async () => {
                if (route.query?.stub) {
                    const resp = await http.get("/api/v2/report/stub/" + route.query.stub, { cancelToken: report.value.cancelToken.token });
                    //report.value = resp.data;
                    //report.value.Id = resp.data.Id;
                    report.value.filter = resp.data.filter;
                }
            };

            const SetBreakdown = (result) => {
                breakdown.value = result;
            };
            const SwitchEditState = () => {
                editFilter.value = !editFilter.value;
            };

            const OpenGlossary = async () => {
                popup.open("How are dashboard metrics calculated?", glossaryTemplate);
            };

            const PrepareColumns = () => {
                const filteredResults = breakdown.value.api.BreakdownFilterResults(report.value.results);
                const metrics = breakdown.value.api.CreateMetrics(filteredResults);
                metrics.sort((a, b) => (a.name > b.name ? 1 : -1));
                reportOptions.value.sheets = [
                    {
                        title: "Metrics",
                        headers: [
                            { key: "totalBillings", value: "Total Billings", options: { type: "number" } },
                            { key: "executive", value: "Executive Search", options: { type: "number" } },
                            { key: "consulting", value: "Leadership Consulting", options: { type: "number" } },
                            { key: "im", value: "Interim Management", options: { type: "number" } },
                            { key: "teamingValue", value: "Teaming Value", options: { type: "number" } },
                            { key: "teamingRevenue", value: "Teaming Revenue", options: { type: "number" } },
                            {
                                key: "filledJobValue",
                                value: "Average Filled Job Value",
                                options: { type: "number" }
                            }
                        ],
                        rows: [
                            {
                                totalBillings: metrics.reduce((acc, row) => {
                                    return acc + row["totalBillings"];
                                }, 0),
                                executive: metrics.reduce((acc, row) => {
                                    return acc + row["executive"];
                                }, 0),
                                consulting: metrics.reduce((acc, row) => {
                                    return acc + row["consulting"];
                                }, 0),
                                im: metrics.reduce((acc, row) => {
                                    return acc + row["im"];
                                }, 0),
                                teamingValue: metrics.reduce((acc, row) => {
                                    return acc + row["teamingValue"];
                                }, 0),
                                teamingRevenue: metrics.reduce((acc, row) => {
                                    return acc + row["teamingRevenue"];
                                }, 0),
                                filledJobValue: $financial.getFilledJobValue(filteredResults)
                            }
                        ]
                    },
                    {
                        title: "Metrics Extended",
                        headers: [
                            { key: "assignmentsTaken", value: "Assignments Taken", options: { type: "number" } },
                            { key: "teamingTaken", value: "Teaming Taken", options: { type: "number" } },
                            { key: "assignmentsFilled", value: "Assignments Filled", options: { type: "number" } },
                            { key: "teamingFilled", value: "Teaming Filled", options: { type: "number" } },
                            { key: "timeToFill", value: "Time to Fill", options: { type: "number" } }
                        ],
                        rows: [
                            {
                                assignmentsTaken: metrics.reduce((acc, row) => {
                                    return acc + row["assignmentsTaken"];
                                }, 0),
                                teamingTaken: metrics.reduce((acc, row) => {
                                    return acc + row["teamingTaken"];
                                }, 0),
                                assignmentsFilled: metrics.reduce((acc, row) => {
                                    return acc + row["assignmentsFilled"];
                                }, 0),
                                teamingFilled: metrics.reduce((acc, row) => {
                                    return acc + row["teamingFilled"];
                                }, 0),
                                timeToFill: $financial.getTimeToFill(filteredResults)
                            }
                        ]
                    },
                    {
                        title: "Boydenian Details",
                        headers: [
                            { key: "partnerChange", value: "Partner Change", options: { type: "number" } },
                            { key: "partner", value: "Partners", options: { type: "number" } },
                            { key: "researcher", value: "Researchers", options: { type: "number" } },
                            { key: "admin", value: "Administration", options: { type: "number" } }
                        ],
                        rows: [
                            {
                                partnerChange:
                                    boydenianDetails.value.partners.currentCount -
                                    boydenianDetails.value.partners.currentCountStart,
                                partner: boydenianDetails.value.partners.currentCount,
                                researcher: boydenianDetails.value.researchers.current,
                                admin: boydenianDetails.value.administration.current
                            }
                        ]
                    }
                ];

                breakdown.value.api.BreakdownTypes.forEach((opt) => {
                    const metrics = breakdown.value.api.CreateMetrics(filteredResults, { view: opt.key });
                    metrics.sort((a, b) => (a.name > b.name ? 1 : -1));
                    reportOptions.value.sheets.push({
                        title: `${opt.value}`,
                        headers: [
                            { key: "name", value: "Name" },
                            {
                                key: "totalBillings",
                                value: "Total Billings",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "teamingValue",
                                value: "Teaming Value",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "teamingRevenue",
                                value: "Teaming Revenue",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "teamingTaken",
                                value: "Teaming Taken",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "teamingFilled",
                                value: "Teaming Filled",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "assignmentsTaken",
                                value: "Assignments Taken",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "assignmentsFilled",
                                value: "Assignments Filled",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "timeToFill",
                                value: "Time to fill",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "executive",
                                value: "Executive Search",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "consulting",
                                value: "Consulting",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "im",
                                value: "Interim Management",
                                options: { type: "number", format: "#,##0.00" }
                            },
                            {
                                key: "filledJobValue",
                                value: "Average Filled Job Value",
                                options: { type: "number", format: "#,##0.00" }
                            }
                        ],
                        rows: metrics
                    });
                });
            };

            const _facade = ref({});
            const BuildFacade = (result) => {
                _facade.value = { ..._facade.value, ...result };
            };

            const GetFacade = async () => {
                const copy = { ...report.value, ...{ results: null, compare: null } };
                try {
                    const response = await http.post("api/v2/financial/report/facade", {
                        report: copy
                    }, { cancelToken: report.value.cancelToken.token });
                    facade.value = response.data;
                } catch (err) {
                    console.error(err);
                }
            };

            const SaveFacade = (timeout) => {
                return debouncer(async () => {
                    if (!breakdown.value.breakdown.breakdownFilter) {
                        const copy = { ...report.value, ...{ results: null, compare: null } };
                        try {
                            await http.post("api/v2/financial/report/facade/save", {
                                report: copy,
                                facade: _facade.value
                            }, { cancelToken: report.value.cancelToken.token });
                        } catch (err) {
                            console.error(err);
                        }
                    }
                }, timeout)();
            };

            const IsFacadeValid = computed(() => {
                if (!_facade.value) return false;

                const errors = [];

                const metrics = _facade.value.metrics;
                if (!metrics) errors.push({ prop: "metrics", message: "Missing property" });

                const compare = _facade.value.compare;
                if (!compare) errors.push({ prop: "compare", message: "Missing property" });

                const details = _facade.value.details;
                if (!details) errors.push({ prop: "details", message: "Missing property" });
                else if (details && Object.keys(details).length == 0)
                    errors.push({ prop: "details", message: "Invalid object" });

                const samples = _facade.value.samples;
                if (!samples) errors.push({ prop: "samples", message: "Missing property" });
                else if (samples && Object.keys(samples).length == 0)
                    errors.push({ prop: "samples", message: "Invalid object" });

                const compare_samples = _facade.value.compare_samples;
                if (!compare_samples) errors.push({ prop: "compare_samples", message: "Missing property" });
                else if (compare_samples && Object.keys(compare_samples).length == 0)
                    errors.push({ prop: "compare_samples", message: "Invalid object" });

                const breakdown = _facade.value.breakdown;
                if (!breakdown) errors.push({ prop: "breakdown", message: "Missing property" });
                else if (breakdown && Object.keys(breakdown).length == 0)
                    errors.push({ prop: "breakdown", message: "Invalid object" });

                // console.log(
                // 	"IsFacadeValid",
                // 	errors.length === 0,
                // 	JSON.parse(JSON.stringify(errors)),
                // 	JSON.parse(JSON.stringify(_facade.value))
                // );
                return errors.length === 0;
            });

            let saving = false;
            watch(
                () => _facade.value,
                async (n, o) => {
                    if (n != o && !saving && IsFacadeValid.value) {
                        saving = true;
                        await SaveFacade(2000);
                        saving = false;
                    }
                }
            );
            watch(
                () => kpi_datasets.value,
                (n, o) => {
                    if (n && Object.keys(n).length >= 0) {
                        BuildFacade({ samples: n });
                    }
                },
                { deep: true }
            );
            watch(
                () => metrics_output.value,
                (n, o) => {
                    if (n) {
                        const metrics_out = { ...(facade.value?.metrics || {}), ...n }; //Prevents overwriting
                        BuildFacade({ ...metrics_out });
                    }
                },
                { deep: true }
            );
            watch(
                () => compare_datasets.value,
                (n, o) => {
                    if (n) {
                        const compare_samples = { ...(facade.value?.compare_samples || {}), ...n }; //Prevents overwriting
                        BuildFacade({ compare_samples: compare_samples });
                    }
                },
                { deep: true }
            );
            watch(
                () => breakdown_output.value,
                (n, o) => {
                    if (n) {
                        const breakdown_out = { ...(facade.value?.breakdown || {}), ...n }; //Prevents overwriting
                        BuildFacade({ breakdown: breakdown_out });
                    }
                },
                { deep: true }
            );

            onMounted(async () => {
                report.value.loading = true;
                report.value.cancelToken = cancellationToken;
                await InitializeReport();
                if (props.widget || report.value.filter?.keys?.length > 0) {
                    await GetReport();
                }

                if (!(props.autoRun && !forceNoAutoRun.value)) {
                    report.value.loading = false;
                }
            });

            return {
                hasRan,
                options,
                report,
                breakdown,
                boydenianDetails,
                editFilter,
                reportLastRun,
                reportOptions,
                facade,
                loadingFacade,
                forceNoAutoRun,
                GetReport,
                SetBreakdown,
                SwitchEditState,
                OpenGlossary,
                AwaitGetReport,
                PrepareColumns,
                BuildFacade
            };
        }
    };
</script>
<style lang="scss" scoped>
    @import "@/assets/css/globalVars.scss";

    .report {
        display: flex;
        flex-direction: column;

        gap: $card-gap;

        & .is-pulled-left.widen {
            width: 60vw;
        }

        & .form-row {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-start;
            justify-content: flex-start;
            gap: $margin-element-medium;
            width: 100%;

            &:nth-child(2) {
                align-items: flex-end;
                justify-content: space-between;
            }
        }

        & .form-row-right {
            justify-content: flex-end;
            align-items: flex-end;

            &:nth-child(2) {
                align-items: flex-end;
                justify-content: flex-end;
            }
        }
    }
</style>
